/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../core/services/qpilot.service";
import { SessionService } from "../../../core/services/session.service";
import { MessageService } from "primeng/api";
import { ValidationResult } from "../../scheduled-orders/interfaces/scheduled-orders-list-item.interface";

/**
 * Service for managing scheduled order validation
 * Handles pre-validation checks and validation result updates
 */
@Injectable({ providedIn: "root" })
export class ScheduledOrderValidationService {
  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
  ) {}

  /**
   * Validate a scheduled order (pre-processing checks)
   * @param siteId Site ID
   * @param scheduledOrder Full scheduled order object
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of validation result
   */
  validate(
    siteId: number,
    scheduledOrder: any, // Full order object required by API
    accessToken?: QPilotAccessToken,
  ): Observable<ValidationResult> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .validateScheduledOrder(
        siteId,
        scheduledOrder, // Pass full order object
        token,
        false,
      )
      .pipe(
        map((result) => {
          // Show validation feedback
          if (!result.Succeeded) {
            this.messageService.add({
              severity: "warn",
              summary: "Validation Issues",
              detail:
                "This order has validation issues that may prevent processing",
              life: 5000,
            });
          }
          return result;
        }),
        catchError((error) => {
          console.error("Validation error:", error);
          this.messageService.add({
            severity: "error",
            summary: "Validation Error",
            detail: "Failed to validate scheduled order",
            life: 5000,
          });

          // Return failed validation result
          return of({
            ScheduledOrderId: scheduledOrder.Id || 0,
            Succeeded: false,
            ErrorItems: [],
            UpdatedUtc: new Date(),
            IsInRepeatTimeWindow: false,
          });
        }),
      );
  }

  /**
   * Get the current validation result for a scheduled order
   * Uses POST endpoint (doesn't persist) to match old QMC behavior
   * OLD QMC: validation-checks-popup-view.component.ts lines 48-58
   * @param siteId Site ID
   * @param scheduledOrder Full scheduled order object
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of validation result
   */
  getValidationResult(
    siteId: number,
    scheduledOrder: any, // Full order object required by API
    accessToken?: QPilotAccessToken,
  ): Observable<ValidationResult> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .getScheduledOrderPreValidationResult(
        siteId,
        scheduledOrder, // Pass full order (matches old QMC)
        token,
        false,
      )
      .pipe(
        catchError((error) => {
          console.error("Get validation result error:", error);

          // Return null result on error (not critical)
          return of({
            ScheduledOrderId: scheduledOrder.Id || 0,
            Succeeded: true,
            ErrorItems: [],
            UpdatedUtc: new Date(),
            IsInRepeatTimeWindow: false,
          });
        }),
      );
  }

  /**
   * Update (refresh) the validation result for a scheduled order
   * Uses POST endpoint (doesn't persist) to match old QMC behavior
   * Old QMC: pre-processing-result-indicator.component.ts line 96
   * @param siteId Site ID
   * @param scheduledOrder Full scheduled order object
   * @param accessToken Optional access token for embedded mode
   * @returns Observable of updated validation result
   */
  updateValidationResult(
    siteId: number,
    scheduledOrder: any, // Full order object required by API
    accessToken?: QPilotAccessToken,
  ): Observable<ValidationResult> {
    const token = accessToken ?? this.sessionService.accessToken;
    if (!token) {
      return throwError(() => new Error("No access token available"));
    }
    return this.qpilotService
      .getScheduledOrderPreValidationResult(
        siteId,
        scheduledOrder, // Pass full order (matches old QMC)
        token,
        false,
      )
      .pipe(
        map((result) => {
          return result;
        }),
        catchError((error) => {
          console.error("Update validation result error:", error);

          // Return current state on error (not critical)
          return of({
            ScheduledOrderId: scheduledOrder.Id || 0,
            Succeeded: true,
            ErrorItems: [],
            UpdatedUtc: new Date(),
            IsInRepeatTimeWindow: false,
          });
        }),
      );
  }

  /**
   * Check if a validation result indicates the order is processable
   * @param validation Validation result
   * @returns true if order can be processed
   */
  isProcessable(validation: ValidationResult | undefined): boolean {
    if (!validation) {
      return true; // No validation result = assume OK
    }

    return validation.Succeeded && !validation.IsInRepeatTimeWindow;
  }

  /**
   * Get user-friendly error messages from validation result
   * @param validation Validation result
   * @returns Array of error messages
   */
  getErrorMessages(validation: ValidationResult | undefined): string[] {
    if (
      !validation ||
      !validation.ErrorItems ||
      validation.ErrorItems.length === 0
    ) {
      return [];
    }

    return validation.ErrorItems.map(
      (error) =>
        error.ScheduledOrderFailureReason ||
        `Error code: ${error.ProcessingErrorCode}`,
    );
  }

  /**
   * Get validation summary for display
   * @param validation Validation result
   * @returns Summary object
   */
  getValidationSummary(validation: ValidationResult | undefined): {
    isValid: boolean;
    message: string;
    severity: "success" | "warn" | "danger";
  } {
    if (!validation) {
      return {
        isValid: true,
        message: "Not validated",
        severity: "warn",
      };
    }

    if (validation.IsInRepeatTimeWindow) {
      return {
        isValid: false,
        message: "Order is in repeat time window",
        severity: "warn",
      };
    }

    if (!validation.Succeeded) {
      const errorCount = validation.ErrorItems?.length || 0;
      return {
        isValid: false,
        message: `${errorCount} validation ${errorCount === 1 ? "issue" : "issues"} found`,
        severity: "danger",
      };
    }

    return {
      isValid: true,
      message: "All validation checks passed",
      severity: "success",
    };
  }
}
